import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import Yup from 'yup';
import ModalAbstraction from '../../CallToAction/LeftRailCTA/ModalAbstraction/ModalAbstraction';
import PopUp from '../../Elements/PopUp/PopUp';
import Button from '../../Input/Button/Button';
import InputField from '../../Input/InputField/InputField';

import './MaternityTipModal.scss';

const typeMap = {
    paidMaternity: 'Paid Maternity',
    unpaidMaternity: 'Unpaid Maternity',
    paidPaternity: 'Paid Paternity',
    unpaidPaternity: 'Unpaid Paternity',
};

const schemas = {
    companyId: Yup.number().required(),
    paidMaternity: Yup.number('Please enter a tip between 0 to 52.')
        .nullable()
        .min(0, 'Please enter a tip between 0 to 52.')
        .max(52, 'Please enter a tip between 0 to 52.'),
    unpaidMaternity: Yup.number('Please enter a tip between 0 to 52.')
        .nullable()
        .min(0, 'Please enter a tip between 0 to 52.')
        .max(52, 'Please enter a tip between 0 to 52.'),
    paidPaternity: Yup.number('Please enter a tip between 0 to 52.')
        .nullable()
        .min(0, 'Please enter a tip between 0 to 52.')
        .max(52, 'Please enter a tip between 0 to 52.'),
    unpaidPaternity: Yup.number('Please enter a tip between 0 to 52.')
        .nullable()
        .min(0, 'Please enter a tip between 0 to 52.')
        .max(52, 'Please enter a tip between 0 to 52.'),
    atLeastOneTip: Yup.mixed().when(
        [
            'paidMaternity',
            'unpaidMaternity',
            'paidPaternity',
            'unpaidPaternity',
        ],
        {
            is: (
                paidMaternity,
                unpaidMaternity,
                paidPaternity,
                unpaidPaternity
            ) => {
                return (
                    (paidMaternity === undefined || paidMaternity === null) &&
                    (unpaidMaternity === undefined ||
                        unpaidMaternity === null) &&
                    (paidPaternity === undefined || paidPaternity === null) &&
                    (unpaidPaternity === undefined || unpaidPaternity === null)
                );
            },
            then: Yup.string().test(
                'at-least-one',
                'Please fill out at least one of these fields.',
                () => false
            ),
            otherwise: Yup.string().test(
                'at-least-one',
                'Please fill out at least one of these fields.',
                () => true
            ),
        }
    ),
};

export default class MaternityTipModal extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            paidMaternity: null,
            unpaidMaternity: null,
            paidPaternity: null,
            unpaidPaternity: null,
            errors: {
                companyId: null, // TODO: if companyId is missing, send back a generalError to user
                paidMaternity: null,
                unpaidMaternity: null,
                paidPaternity: null,
                unpaidPaternity: null,
                atLeastOneTip: null,
            },
            generalError: null,
            showPopUp: false,
            popUpMessage: null,
        };
    }

    static propTypes = {
        companyId: PropTypes.number.isRequired,
        companyName: PropTypes.string.isRequired,
        onClose: PropTypes.func.isRequired,
        showModal: PropTypes.bool.isRequired,
    };

    _validateInput = async (type, value) => {
        try {
            await schemas[type].validate(value);
            return true;
        } catch (e) {
            const errors = { ...this.state.errors };
            errors[type] = e.message;
            this.setState({
                errors,
            });
            return false;
        }
    };

    handleTipChange = (type) => (value) => {
        const newState = { ...this.state };
        const newValue = value ? Number(value) : null;
        newState[type] = newValue;
        newState.errors[type] = null;
        newState.generalError = null;
        this.setState(newState, () => this._validateInput(type, newValue));
    };

    onSubmitTipForm = async () => {
        try {
            await axios.post('/api/company/submit-maternity-tip', {
                companyId: this.props.companyId,
                paidMaternity: this.state.paidMaternity,
                unpaidMaternity: this.state.unpaidMaternity,
                paidPaternity: this.state.paidPaternity,
                unpaidPaternity: this.state.unpaidPaternity,
            });

            this.setState({
                showPopUp: true,
                popUpMessage:
                    "Thanks for the tip. We'll review and post content that meets guidelines shortly.",
            });
        } catch (e) {
            const errors = e.response.data.errors;
            const errorMessage = errors[Object.keys(errors)[0]];

            this.setState({
                showPopUp: true,
                popUpMessage: errorMessage,
            });
        }
    };

    renderTipField = (type) => {
        return (
            <div className="input-tip-section">
                <InputField
                    name={type}
                    type={'number'}
                    qaDataAttr={type}
                    onChange={this.handleTipChange(type)}
                    onBlur={() => this._validateInput(type, this.state[type])}
                    value={this.state[type]}
                    placeholder={`Please enter ${typeMap[type]} Leave (weeks)`}
                />
                <div className="errorMessage">{this.state.errors[type]}</div>
            </div>
        );
    };

    renderForm = () => {
        const {
            paidMaternity,
            unpaidMaternity,
            paidPaternity,
            unpaidPaternity,
            errors,
            generalError,
        } = this.state;
        let noErrors = true,
            submitButtonDisabled = true;
        if (generalError) {
            noErrors = false;
        }
        for (let type in errors) {
            if (errors[type]) {
                noErrors = false;
                break;
            }
        }

        if (
            noErrors &&
            (paidMaternity ||
                unpaidMaternity ||
                paidPaternity ||
                unpaidPaternity)
        ) {
            submitButtonDisabled = false;
        }

        return (
            <div className={'maternity-tip-form'}>
                <form>
                    {this.renderTipField('paidMaternity')}
                    {this.renderTipField('unpaidMaternity')}
                    {this.renderTipField('paidPaternity')}
                    {this.renderTipField('unpaidPaternity')}
                </form>
                <div className="submit-button-div">
                    <Button
                        disabled={submitButtonDisabled}
                        type={'solid'}
                        value={'Add a tip'}
                        onClick={this.onSubmitTipForm}
                    />
                </div>
            </div>
        );
    };

    render() {
        return (
            <div className="component-Employer-MaternityTipModal">
                <ModalAbstraction
                    showModal={this.props.showModal}
                    onClose={this.props.onClose}
                    allowScroll={false}
                    showButton={false}
                    title={`ADD A TIP FOR ${this.props.companyName.toUpperCase()}!`}
                >
                    {this.renderForm()}
                </ModalAbstraction>
                <PopUp
                    showPopUp={this.state.showPopUp}
                    showOverlay={true}
                    close={() => this.setState({ showPopUp: false })}
                    cancel={() => this.setState({ showPopUp: false })}
                    buttonText={'Continue'}
                    minHeight={'300px'}
                    minWidth={'320px'}
                    maxHeight={'300px'}
                    maxWidth={'320px'}
                >
                    <p>{this.state.popUpMessage}</p>
                </PopUp>
            </div>
        );
    }
}
